/*! spx-catalog.js — THE catalog engine (CATALOG_REBUILD_PLAN §2 assets).
 * Single owner of all catalog interactivity on sections/sp-catalog-grid:
 *   - filter drawer open/close (lazy <template> clone into [data-sp-drawer-host];
 *     re-cloned fresh after every section swap so state = server truth)
 *   - any facet control -> URLSearchParams delta (page deleted) ->
 *     fetch {collection.url}?{params}&section_id=<id> (Section Rendering API) ->
 *     swap section innerHTML -> history.pushState (pretty single-metal URL rule) ->
 *     dispatch native 'collection:reloaded' -> window.spIfsInit() if present
 *   - popstate -> re-render from URL (param AND pretty tag-page forms)
 *   - graceful full-reload fallback on ANY fetch/parse error (failures never cached)
 *   - legacy inbound param shim: ?filter=/?metal=/?shape=/?carat= -> native params
 *     via ONE replaceState on load (rg-family vocabulary, plan §6 step 5)
 * Vanilla ES5, IIFE, no jQuery. Delegated document-level listeners survive swaps.
 */
(function () {
  'use strict';

  if (window.__spxCatalogInit) return;

  var ROOT_ID = 'sp-catalog-grid';
  var FILTER_PREFIX = 'filter.';
  var PRESERVED_ON_CLEAR = ['sort_by', 'q']; /* non-filter params kept by CLEAR ALL */
  var PRICE_DEBOUNCE_MS = 500;

  function getRoot() { return document.getElementById(ROOT_ID); }
  var rootEl = getRoot();
  if (!rootEl) return; /* not a catalog page */
  window.__spxCatalogInit = true;

  /* ---------------- utils ---------------- */
  function matchesSel(el, sel) {
    if (!el || el.nodeType !== 1) return false;
    var fn = el.matches || el.msMatchesSelector || el.webkitMatchesSelector;
    return fn ? fn.call(el, sel) : false;
  }
  function closestSel(el, sel) {
    while (el && el.nodeType === 1) {
      if (matchesSel(el, sel)) return el;
      el = el.parentNode;
    }
    return null;
  }
  function handleize(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
  }
  function cloneParams(p) { return new URLSearchParams(p.toString()); }
  function hasPair(p, name, value) {
    var all = p.getAll(name);
    for (var i = 0; i < all.length; i++) { if (all[i] === value) return true; }
    return false;
  }
  function removePair(p, name, value) {
    var all = p.getAll(name);
    p.delete(name);
    for (var i = 0; i < all.length; i++) { if (all[i] !== value) p.append(name, all[i]); }
  }

  /* ---------------- section context ---------------- */
  var sectionEl = closestSel(rootEl, '.shopify-section');
  if (!sectionEl || !sectionEl.id) return;
  var sectionId = sectionEl.id.replace(/^shopify-section-/, '');
  var baseUrl = (rootEl.getAttribute('data-collection-url') || window.location.pathname).replace(/\/+$/, '');
  var defaultSort = rootEl.getAttribute('data-default-sort') || '';
  var metalParam = rootEl.getAttribute('data-metal-param') || '';

  function readFacetMap() {
    var r = getRoot();
    var node = r && r.querySelector('script[data-sp-facet-map]');
    if (!node) return {};
    try { return JSON.parse(node.textContent) || {}; } catch (e) { return {}; }
  }
  var facetMap = readFacetMap();

  var seq = 0;
  var drawerOpen = false;
  var drawerScrollTop = 0;
  var drawerNode = null; /* persistent drawer element — lives in <body>, survives section swaps (2026-07-09 freeze fix) */
  var priceTimer = null;

  /* ---------------- URL state ---------------- */
  function paramsFromLocation() {
    var p = new URLSearchParams(window.location.search);
    p.delete('section_id');
    p.delete('_t');
    /* pretty single-metal tag landing: server seeds data-metal-tag-param="name=value" */
    var r = getRoot();
    var seeded = r && r.getAttribute('data-metal-tag-param');
    if (seeded) {
      var idx = seeded.indexOf('=');
      if (idx > 0) {
        var n = seeded.slice(0, idx);
        var v = seeded.slice(idx + 1);
        if (!hasPair(p, n, v)) p.append(n, v);
      }
    }
    return p;
  }
  var state = paramsFromLocation();

  /* pretty single-metal URL rule (ported from seo-filter, now param-honest):
     exactly ONE filter param total and it is the metal param
     -> /collections/{handle}/{metal-handle} (sort_by/q ride along as query) */
  function displayUrl(p) {
    var filterPairs = [];
    p.forEach(function (v, k) { if (k.indexOf(FILTER_PREFIX) === 0) filterPairs.push([k, v]); });
    if (filterPairs.length === 1 && metalParam && filterPairs[0][0] === metalParam) {
      var rest = cloneParams(p);
      rest.delete(metalParam);
      rest.delete('page');
      var qs = rest.toString();
      return baseUrl + '/' + handleize(filterPairs[0][1]) + (qs ? '?' + qs : '');
    }
    var qs2 = p.toString();
    return baseUrl + (qs2 ? '?' + qs2 : '');
  }

  function fetchUrl(p) {
    var q = cloneParams(p);
    q.set('section_id', sectionId);
    return baseUrl + '?' + q.toString();
  }

  /* ---------------- render / swap ---------------- */
  function setLoading(on) {
    var r = getRoot();
    if (!r) return;
    if (on) { r.classList.add('is-loading'); r.setAttribute('aria-busy', 'true'); }
    else { r.classList.remove('is-loading'); r.removeAttribute('aria-busy'); }
  }

  function swapFromHtml(html) {
    var doc;
    try { doc = new DOMParser().parseFromString(html, 'text/html'); } catch (e) { return false; }
    if (!doc || !doc.getElementById(ROOT_ID)) return false;
    var freshSection = doc.getElementById(sectionEl.id);
    sectionEl.innerHTML = freshSection ? freshSection.innerHTML : doc.body.innerHTML;
    return true;
  }

  function afterSwap() {
    var r = getRoot();
    if (!r) return;
    facetMap = readFacetMap();
    metalParam = r.getAttribute('data-metal-param') || metalParam;
    /* inline scripts in swapped innerHTML never execute — re-emit the compat global here */
    var total = parseInt(r.getAttribute('data-sp-total'), 10);
    if (!isNaN(total)) window._spCollectionTotal = total;
    fireReloaded();
    if (typeof window.spIfsInit === 'function') { try { window.spIfsInit(); } catch (e) {} }
    /* persistent drawer survives the swap — refresh its counts + checked states in place
       (no destroy/re-clone). Its open/closed state is untouched, so it can't get stuck. */
    syncDrawer(total);
  }

  /* keep the persistent drawer's result count + facet checked states matching the applied
     filters after a re-render (the drawer element itself is never re-rendered from server). */
  function syncDrawer(total) {
    if (!drawerNode) return;
    if (!isNaN(total)) {
      var counts = drawerNode.querySelectorAll('[data-sp-count]');
      for (var i = 0; i < counts.length; i++) { counts[i].textContent = total; }
    }
    var inputs = drawerNode.querySelectorAll('input[data-sp-filter]');
    for (var j = 0; j < inputs.length; j++) {
      var nm = inputs[j].getAttribute('name');
      if (!nm) continue;
      var vals = valuesOf(inputs[j]);
      var on = false;
      for (var k = 0; k < vals.length; k++) { if (hasPair(state, nm, vals[k])) { on = true; break; } }
      inputs[j].checked = on;
      /* The SELECTED LOOK of a shape/style tile comes from .is-active, which Liquid stamps once
         when the drawer is first rendered. The drawer is persistent (deliberately never
         re-rendered, so it can't get stuck open), so that class goes stale the moment a tile is
         toggled: the filter clears correctly but the tile keeps its navy border and still reads
         as selected. Keep the class in step with the real checked state. */
      var lab = closestSel(inputs[j], '.sp-drawer__shape-tile') || closestSel(inputs[j], '.sp-drawer__style-tile');
      if (lab) {
        if (on) { lab.classList.add('is-active'); } else { lab.classList.remove('is-active'); }
      }
    }
  }

  function fireReloaded() {
    var ev;
    try {
      ev = new CustomEvent('collection:reloaded', { bubbles: true });
    } catch (e) {
      ev = document.createEvent('CustomEvent');
      ev.initCustomEvent('collection:reloaded', true, false, null);
    }
    document.dispatchEvent(ev);
  }

  var prefetchCache = {};
  function getHtml(p) {
    var url = fetchUrl(p);
    if (!prefetchCache[url]) {
      prefetchCache[url] = fetch(url, { headers: { Accept: 'text/html' } })
        .then(function (res) { if (!res.ok) { throw new Error('HTTP ' + res.status); } return res.text(); })
        .catch(function (e) { delete prefetchCache[url]; throw e; });
    }
    return prefetchCache[url];
  }
  /* warm a facet result the shopper is about to tap (pointerenter/touchstart) so the swap
     feels instant — makes shape/filter switching "liquid" (owner 2026-07-09). */
  function prefetch(p) { try { getHtml(p); } catch (e) {} }

  function render(p, opts) {
    opts = opts || {};
    seq += 1;
    var mySeq = seq;
    /* OPTIMISTIC UPDATE: commit state + drawer checkbox states SYNCHRONOUSLY at tap time.
       Only the grid PAINT (swap) stays async + seq-guarded below. This kills the race where,
       on a slow connection, a rapid second tap read a STALE `state` (state used to be assigned
       only after the fetch resolved) — leaving the first shape stuck-checked while a later
       render's URL had already moved on (owner 2026-07-09 "the first diamond gets stuck").
       `state` is now the single source of truth the moment you tap, so back-to-back taps
       (and the single-select clear) are always consistent regardless of network timing. */
    state = cloneParams(p);
    captureDrawerScroll();
    syncDrawer(NaN);   /* reflect single-select clearing on the persistent drawer now; counts refresh after swap */
    setLoading(true);
    var target = displayUrl(p);
    getHtml(p)
      .then(function (html) {
        if (mySeq !== seq) return;   /* superseded by a newer tap — drop this paint */
        if (!swapFromHtml(html)) { throw new Error('sp-catalog root missing in section response'); }
        if (opts.push !== false) {
          try { history.pushState({ spCatalog: true }, '', target); } catch (e) {}
        }
        afterSwap();
      })
      .catch(function () {
        if (mySeq !== seq) return;
        /* graceful degradation: real navigation to the same state (nothing cached) */
        window.location.assign(target);
      });
  }

  /* ---------------- drawer (template-based lazy render) ---------------- */
  function drawerHost() { var r = getRoot(); return r ? r.querySelector('[data-sp-drawer-host]') : null; }
  function drawerEl() { return (drawerNode && drawerNode.isConnected) ? drawerNode : null; }

  /* Clone the drawer ONCE into <body> and reuse it. Living outside the section means a
     filter re-render (which swaps the section innerHTML) never destroys or re-clones it —
     kills the re-clone race that could leave the drawer stuck covering the screen. */
  function ensureDrawer() {
    if (drawerNode && drawerNode.isConnected) return drawerNode;
    var r = getRoot();
    var tpl = r && r.querySelector('template[data-sp-drawer-template]');
    if (!tpl) return null;
    var holder = document.createElement('div');
    if (tpl.content) holder.appendChild(tpl.content.cloneNode(true));
    else holder.innerHTML = tpl.innerHTML; /* template-less UA fallback */
    var node = holder.querySelector('.sp-drawer') || holder.firstElementChild;
    if (!node) return null;
    document.body.appendChild(node);
    drawerNode = node;
    return drawerNode;
  }

  function captureDrawerScroll() {
    var d = drawerEl();
    if (!d) return;
    var body = d.querySelector('.sp-drawer__body');
    if (body) drawerScrollTop = body.scrollTop;
  }

  function setExpanded(v) {
    var r = getRoot();
    if (!r) return;
    var btns = r.querySelectorAll('[data-sp-drawer-open]');
    for (var i = 0; i < btns.length; i++) { btns[i].setAttribute('aria-expanded', v ? 'true' : 'false'); }
  }

  function openDrawer(opts) {
    opts = opts || {};
    var d = ensureDrawer();
    if (!d) return;
    /* the drawer is cloned once from the server template (initial state); if the shopper
       changed filters via a rail/pill before ever opening it, reconcile its checkboxes to
       the live `state` on open so it never shows a stale selection. */
    syncDrawer(parseInt((getRoot() || {}).getAttribute ? getRoot().getAttribute('data-sp-total') : 'NaN', 10));
    drawerOpen = true;
    if (opts.instant) {
      d.classList.add('no-anim');
      d.classList.add('is-open');
      window.setTimeout(function () { d.classList.remove('no-anim'); }, 60);
    } else {
      d.classList.add('is-open');
    }
    document.documentElement.classList.add('sp-drawer-lock');
    setExpanded(true);
    if (opts.restoreScroll) {
      var body = d.querySelector('.sp-drawer__body');
      if (body) body.scrollTop = drawerScrollTop;
    }
    if (!opts.instant) {
      var panel = d.querySelector('.sp-drawer__panel');
      if (panel) {
        panel.setAttribute('tabindex', '-1');
        try { panel.focus({ preventScroll: true }); } catch (e) { try { panel.focus(); } catch (e2) {} }
      }
    }
  }

  function closeDrawer() {
    drawerOpen = false;
    var d = drawerEl();
    if (d) d.classList.remove('is-open');
    document.documentElement.classList.remove('sp-drawer-lock');
    setExpanded(false);
    var r = getRoot();
    var btn = r && r.querySelector('[data-sp-drawer-open]');
    if (btn) { try { btn.focus(); } catch (e) {} }
  }

  /* ---------------- facet mutations ---------------- */
  /* merged display entries (sp-catalog-display-map): one control applies ALL mapped raw
     values as repeated params — S&D ORs repeated values of the same param (verified:
     Antique Cut 1 + Antique Cuts 25 + Antique_Cuts 1 -> 27). data-sp-values = JSON array. */
  function valuesOf(el) {
    var raw = el.getAttribute('data-sp-values');
    if (raw) {
      try {
        var arr = JSON.parse(raw);
        if (arr && arr.length) return arr;
      } catch (e) {}
    }
    return [el.value];
  }

  /* rail quick-filter tiles: one value (data-sp-facet-value) or many (data-sp-facet-values
     JSON array — e.g. a metal family = 14k + 18k karat values). */
  function facetTileVals(el) {
    var raw = el.getAttribute('data-sp-facet-values');
    if (raw) {
      try { var a = JSON.parse(raw); if (a && a.length) return a; } catch (e) {}
    }
    var v = el.getAttribute('data-sp-facet-value');
    return v ? [v] : [];
  }

  function applyCheckbox(input) {
    var name = input.getAttribute('name');
    if (!name) return;
    var p = cloneParams(state);
    /* SINGLE-SELECT groups: Metal (one metal at a time) + Shape (one diamond shape at a time,
       BE-style — keeps the funnel clean/concise). Checking one clears the rest in the same group.
       Toggling the active one off still works (checked=false). Style stays multi-select. */
    /* Rings' shape is a VARIANT OPTION and stays single-select (BE funnel, deliberate).
       Earrings' shape is a PRODUCT METAFIELD and is multi-select, matching the style tiles -
       owner asked to be able to pick several at once. */
    var shapeGroup = closestSel(input, '.sp-drawer__group--shape');
    if (shapeGroup && (name || '').indexOf('custom.shape') !== -1) shapeGroup = null;
    var singleGroup = closestSel(input, '.sp-drawer__group--metal') || shapeGroup;
    if (singleGroup && input.checked) {
      var groupInputs = singleGroup.querySelectorAll('input[data-sp-filter]');
      for (var mi = 0; mi < groupInputs.length; mi++) {
        var mn = groupInputs[mi].getAttribute('name');
        if (!mn) continue;
        var mv = valuesOf(groupInputs[mi]);
        for (var mj = 0; mj < mv.length; mj++) removePair(p, mn, mv[mj]);
        if (groupInputs[mi] !== input) groupInputs[mi].checked = false; /* reflect single-select immediately */
      }
    }
    var vals = valuesOf(input);
    for (var i = 0; i < vals.length; i++) {
      if (input.checked) { if (!hasPair(p, name, vals[i])) p.append(name, vals[i]); }
      else { removePair(p, name, vals[i]); }
    }
    p.delete('page');
    render(p);
  }

  function applySort(select) {
    var p = cloneParams(state);
    var v = select.value;
    if (v && v !== defaultSort) p.set('sort_by', v);
    else p.delete('sort_by');
    p.delete('page');
    render(p);
  }

  function setOrDelete(p, el) {
    if (!el) return;
    var name = el.getAttribute('name');
    if (!name) return;
    var v = String(el.value || '').replace(/[^0-9.]/g, '');
    if (v !== '') p.set(name, v);
    else p.delete(name);
  }

  function applyPrice() {
    var scope = drawerEl() || getRoot();
    if (!scope) return;
    var p = cloneParams(state);
    setOrDelete(p, scope.querySelector('input[data-sp-price="gte"]'));
    setOrDelete(p, scope.querySelector('input[data-sp-price="lte"]'));
    p.delete('page');
    render(p);
  }

  function applyBucket(btn) {
    var wrap = closestSel(btn, '[data-gte-name]');
    if (!wrap) return;
    var gteName = wrap.getAttribute('data-gte-name');
    var lteName = wrap.getAttribute('data-lte-name');
    if (!gteName || !lteName) return;
    var p = cloneParams(state);
    if (btn.classList.contains('is-active')) {
      /* clicking the active bucket clears the price facet */
      p.delete(gteName);
      p.delete(lteName);
    } else {
      var gte = btn.getAttribute('data-gte');
      var lte = btn.getAttribute('data-lte');
      if (gte) p.set(gteName, gte); else p.delete(gteName);
      if (lte) p.set(lteName, lte); else p.delete(lteName);
    }
    p.delete('page');
    render(p);
  }

  /* href (pill / quick-filter / pretty URL) -> params, resolving pretty metal segments */
  function paramsFromHref(href) {
    var a = document.createElement('a');
    a.href = href;
    var p = new URLSearchParams(a.search);
    p.delete('section_id');
    var path = a.pathname.replace(/\/+$/, '');
    if (path.indexOf(baseUrl + '/') === 0) {
      var seg = path.slice(baseUrl.length + 1);
      if (seg && facetMap.metal && facetMap.metal.param && facetMap.metal.values && facetMap.metal.values[seg]) {
        if (!hasPair(p, facetMap.metal.param, facetMap.metal.values[seg])) {
          p.append(facetMap.metal.param, facetMap.metal.values[seg]);
        }
      }
    }
    return p;
  }

  function applyClear() {
    var p = new URLSearchParams();
    for (var i = 0; i < PRESERVED_ON_CLEAR.length; i++) {
      var v = state.get(PRESERVED_ON_CLEAR[i]);
      if (v) p.set(PRESERVED_ON_CLEAR[i], v);
    }
    render(p);
  }

  /* Params that clicking (or prefetching) a quick-filter tile would produce. Shared by the
     click handler + the touch/hover prefetch so both stay in lockstep. Metal/shape are
     single-select (clear siblings in the same rail); style is additive. */
  function facetLinkParams(hit) {
    var fName = hit.getAttribute('data-sp-facet-name');
    var fVals = facetTileVals(hit);
    if (!fName || !fVals.length) {
      var p0 = paramsFromHref(hit.getAttribute('href') || baseUrl);
      p0.delete('page');
      return p0;
    }
    var pT = cloneParams(state);
    var wasActive = true;
    for (var wi = 0; wi < fVals.length; wi++) { if (!hasPair(pT, fName, fVals[wi])) { wasActive = false; break; } }
    var railEl = closestSel(hit, '[data-sp-style-rail]');
    var singleSelect = /option\.(color|metal|shape)/.test(fName)
      || !!(railEl && railEl.getAttribute('data-sp-rail-single') === '1');
    if (railEl && singleSelect) {
      var tiles = railEl.querySelectorAll('a[data-sp-facet-link]');
      for (var ti = 0; ti < tiles.length; ti++) {
        if (tiles[ti].getAttribute('data-sp-facet-name') !== fName) continue;
        var tvs = facetTileVals(tiles[ti]);
        for (var tj = 0; tj < tvs.length; tj++) removePair(pT, fName, tvs[tj]);
      }
      if (!wasActive) { for (var ai = 0; ai < fVals.length; ai++) pT.append(fName, fVals[ai]); }
    } else {
      if (wasActive) { for (var ri = 0; ri < fVals.length; ri++) removePair(pT, fName, fVals[ri]); }
      else { for (var aj = 0; aj < fVals.length; aj++) pT.append(fName, fVals[aj]); }
    }
    pT.delete('page');
    return pT;
  }

  /* Params that TOGGLING a drawer facet checkbox would produce — predicted at touchstart
     (before the browser flips .checked), so we can PREFETCH the result and make in-drawer
     shape/metal switching snappy. Mirrors applyCheckbox's single-select logic. */
  function checkboxParams(input) {
    var name = input.getAttribute('name');
    if (!name) return null;
    var willCheck = !input.checked;
    var p = cloneParams(state);
    /* Rings' shape is a VARIANT OPTION and stays single-select (BE funnel, deliberate).
       Earrings' shape is a PRODUCT METAFIELD and is multi-select, matching the style tiles -
       owner asked to be able to pick several at once. */
    var shapeGroup = closestSel(input, '.sp-drawer__group--shape');
    if (shapeGroup && (name || '').indexOf('custom.shape') !== -1) shapeGroup = null;
    var singleGroup = closestSel(input, '.sp-drawer__group--metal') || shapeGroup;
    if (singleGroup && willCheck) {
      var groupInputs = singleGroup.querySelectorAll('input[data-sp-filter]');
      for (var mi = 0; mi < groupInputs.length; mi++) {
        var mn = groupInputs[mi].getAttribute('name');
        if (!mn) continue;
        var mv = valuesOf(groupInputs[mi]);
        for (var mj = 0; mj < mv.length; mj++) removePair(p, mn, mv[mj]);
      }
    }
    var vals = valuesOf(input);
    for (var i = 0; i < vals.length; i++) {
      if (willCheck) { if (!hasPair(p, name, vals[i])) p.append(name, vals[i]); }
      else { removePair(p, name, vals[i]); }
    }
    p.delete('page');
    return p;
  }

  /* warm the result for whatever facet control the shopper just touched/hovered (rail tile OR
     drawer checkbox) so the swap is near-instant. Returns the matched control (for hover dedupe). */
  function prefetchTarget(t) {
    if (t && t.nodeType === 3) t = t.parentNode;
    var link = closestSel(t, 'a[data-sp-facet-link]');
    if (link) { prefetch(facetLinkParams(link)); return link; }
    var lbl = closestSel(t, 'label');
    if (lbl) {
      var inp = lbl.querySelector('input[data-sp-filter]');
      if (inp) { var cp = checkboxParams(inp); if (cp) prefetch(cp); return lbl; }
    }
    return null;
  }

  /* ---------------- legacy inbound param shim (?filter=/?metal=/?shape=/?carat=) ---------------- */
  function legacyShim() {
    if (!/[?&](filter|metal|shape|carat)=/.test(window.location.search)) return false;
    var legacy = new URLSearchParams(window.location.search);
    var native = cloneParams(state);
    var changed = false;
    var defs = [
      { key: 'filter', facet: 'tag' },
      { key: 'metal', facet: 'metal' },
      { key: 'shape', facet: 'shape' },
      { key: 'carat', facet: 'carat' }
    ];
    for (var i = 0; i < defs.length; i++) {
      var raw = legacy.get(defs[i].key);
      if (!raw) continue;
      changed = true;
      var def = facetMap[defs[i].facet];
      if (def && def.param && def.values) {
        var vals = raw.split(',');
        for (var j = 0; j < vals.length; j++) {
          var mapped = def.values[handleize(vals[j])];
          if (mapped && !hasPair(native, def.param, mapped)) native.append(def.param, mapped);
          /* unmapped legacy value: dropped (facet absent server-side) — TODO-VERIFY against step-0 facet table */
        }
      }
    }
    if (!changed) return false;
    native.delete('filter');
    native.delete('metal');
    native.delete('shape');
    native.delete('carat');
    native.delete('page');
    state = native;
    try { history.replaceState({ spCatalog: true }, '', displayUrl(native)); } catch (e) {}
    render(native, { push: false });
    return true;
  }

  /* ---------------- diamond-in-progress shape seed (owner 2026-07-11) ---------------- */
  /* Returning to the settings catalog mid-build should land where the shopper left off:
     a _diamonmd cookie pre-applies its shape as the filter — but ONLY when the URL carries
     no filter at all. Any explicit filter (add-to-ring link, drawer choice, pretty metal
     URL) outranks the cookie, so changing the shape here works and sticks. Runs once per
     page load; unchecking a filter mid-session never re-seeds. */
  function diamondShapeSeed() {
    var hasFilter = false;
    state.forEach(function (v, k) { if (k.indexOf(FILTER_PREFIX) === 0) hasFilter = true; });
    if (hasFilter) return;
    var m = document.cookie.match(/(^|; ?)_diamonmd=([^;]+)/);
    if (!m) return;
    var shape = '';
    try { shape = JSON.parse(decodeURIComponent(m[2])).shape || ''; } catch (e) { return; }
    /* diamond cookies carry the raw canonical shape — map into the SETTING option
       vocabulary (same table as the setting PDP's last-pick-wins compare) */
    var s = String(shape).toLowerCase().replace(/_/g, ' ').replace(/\s+/g, ' ').trim();
    if (!s) return;
    if (s.indexOf('antique') === 0) s = 'old mine';
    else if (s === 'french marquise') s = 'marquise';
    else if (s === 'portuguse') s = 'portuguese';
    var def = facetMap.shape;
    var mapped = def && def.param && def.values ? def.values[handleize(s)] : null;
    if (!mapped) return; /* shape has no settings facet value — leave unfiltered */
    var p = cloneParams(state);
    p.append(def.param, mapped);
    state = p;
    try { history.replaceState({ spCatalog: true }, '', displayUrl(p)); } catch (e) {}
    render(p, { push: false });
  }

  /* STYLE-collection landing seed (owner 2026-07-15): a bare/Google visit to a style
     collection (solitaire/halo/…) carries no tag facet, so the server marks the root with
     data-sp-default-tag="Solitaire". Apply it in-place so the visitor lands on that style —
     the collection stays FULL so the filter is still switchable (un-check -> all, pick Floral
     -> floral). Skips when any filter is already active (visitor pick / shared URL). */
  function styleTagSeed() {
    var hasFilter = false;
    state.forEach(function (v, k) { if (k.indexOf(FILTER_PREFIX) === 0) hasFilter = true; });
    if (hasFilter) return;
    var root = getRoot();
    var tag = root && root.getAttribute('data-sp-default-tag');
    if (!tag) return;
    var param = (facetMap.tag && facetMap.tag.param) || 'filter.p.tag';
    var p = cloneParams(state);
    p.append(param, tag);
    state = p;
    try { history.replaceState({ spCatalog: true }, '', displayUrl(p)); } catch (e) {}
    render(p, { push: false });
  }

  /* ---------------- delegated events (survive section swaps) ---------------- */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (t && t.nodeType === 3) t = t.parentNode;
    var hit;
    if (closestSel(t, '[data-sp-drawer-open]')) { e.preventDefault(); openDrawer(); return; }
    if (closestSel(t, '[data-sp-drawer-apply]')) { e.preventDefault(); closeDrawer(); return; }
    if (closestSel(t, '[data-sp-drawer-close]')) { e.preventDefault(); closeDrawer(); return; }
    /* BE-style collapsible filter groups: clicking a legend collapses/expands its group */
    if ((hit = closestSel(t, '.sp-drawer__legend'))) {
      var grp = closestSel(hit, '.sp-drawer__group');
      if (grp) { e.preventDefault(); grp.classList.toggle('is-collapsed'); }
      return;
    }
    if ((hit = closestSel(t, 'button[data-sp-bucket]'))) { e.preventDefault(); applyBucket(hit); return; }
    if ((hit = closestSel(t, 'a[data-sp-clear]'))) { e.preventDefault(); applyClear(); return; }
    if ((hit = closestSel(t, 'a[data-sp-pill]'))) {
      e.preventDefault();
      var removeName = hit.getAttribute('data-sp-remove-name');
      if (removeName) {
        /* merged pill: remove EVERY mapped raw value pair (no-JS href removes only one) */
        var removeVals = [];
        try { removeVals = JSON.parse(hit.getAttribute('data-sp-remove-values') || '[]'); } catch (err) { removeVals = []; }
        var pM = cloneParams(state);
        for (var ri = 0; ri < removeVals.length; ri++) { removePair(pM, removeName, removeVals[ri]); }
        pM.delete('page');
        render(pM);
        return;
      }
      var p = paramsFromHref(hit.getAttribute('href') || baseUrl);
      p.delete('page');
      render(p);
      return;
    }
    if ((hit = closestSel(t, 'a[data-sp-facet-link]'))) {
      /* quick-filter tiles (shop-by-style + shop-by-shape rails). Single-select for metal/shape,
         additive for style — computed by facetLinkParams (shared with the prefetch below). */
      /* Rail anchored to a wider collection than the one we're on (e.g. we're on
         /stud-earrings but the rail is anchored to all earrings): let the browser follow the
         href instead of filtering in place, which would strand the shopper on 0 results. */
      var railNav = closestSel(hit, '[data-sp-style-rail]');
      var railBase = railNav && railNav.getAttribute('data-sp-rail-base');
      if (railBase && railBase.replace(/\/+$/, '') !== baseUrl) { return; }
      e.preventDefault();
      render(facetLinkParams(hit));
      return;
    }
  }, false);

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (matchesSel(t, 'input[data-sp-filter]')) { applyCheckbox(t); return; }
    if (matchesSel(t, 'select[data-sp-sort]')) { applySort(t); return; }
    if (matchesSel(t, 'input[data-sp-price]')) {
      if (priceTimer) window.clearTimeout(priceTimer);
      priceTimer = window.setTimeout(applyPrice, PRICE_DEBOUNCE_MS);
      return;
    }
  }, false);

  document.addEventListener('submit', function (e) {
    var form = closestSel(e.target, 'form[data-sp-filter-form]');
    if (!form) return;
    e.preventDefault();
    var p = new URLSearchParams();
    for (var i = 0; i < PRESERVED_ON_CLEAR.length; i++) {
      var v = state.get(PRESERVED_ON_CLEAR[i]);
      if (v) p.set(PRESERVED_ON_CLEAR[i], v);
    }
    var fd = null;
    try { fd = new FormData(form); } catch (err) { fd = null; }
    if (fd && fd.forEach) {
      fd.forEach(function (val, key) { if (String(val) !== '') p.append(key, val); });
    }
    /* merged entries: FormData carries only the primary value — append the rest */
    var mergedInputs = form.querySelectorAll('input[data-sp-values]');
    for (var mi = 0; mi < mergedInputs.length; mi++) {
      if (!mergedInputs[mi].checked) continue;
      var mName = mergedInputs[mi].getAttribute('name');
      if (!mName) continue;
      var mVals = valuesOf(mergedInputs[mi]);
      for (var mj = 0; mj < mVals.length; mj++) {
        if (!hasPair(p, mName, mVals[mj])) p.append(mName, mVals[mj]);
      }
    }
    render(p);
  }, false);

  /* prefetch a filter result the moment the shopper touches/hovers a control — rail tile OR
     drawer checkbox — so the change lands near-instantly (in-drawer shape switching feels
     snappy). touchstart fires once per tap; mouseover is deduped to one prefetch per control. */
  document.addEventListener('touchstart', function (e) { prefetchTarget(e.target); }, { passive: true });
  var _pfLast = null;
  document.addEventListener('mouseover', function (e) {
    var t = e.target; if (t && t.nodeType === 3) t = t.parentNode;
    var cand = closestSel(t, 'a[data-sp-facet-link]') || closestSel(t, 'label');
    if (cand && cand !== _pfLast) { _pfLast = cand; prefetchTarget(cand); }
  }, false);

  document.addEventListener('keydown', function (e) {
    if (!drawerOpen) return;
    var k = e.key || e.keyCode;
    if (k === 'Escape' || k === 'Esc' || k === 27) closeDrawer();
  }, false);

  /* back/forward: re-render from the URL (param AND pretty tag-page forms both work
     against the Section Rendering API) */
  window.addEventListener('popstate', function () {
    if (!getRoot()) return;
    seq += 1;
    var mySeq = seq;
    setLoading(true);
    var loc = window.location;
    var joined = loc.pathname + loc.search + (loc.search ? '&' : '?') + 'section_id=' + sectionId;
    fetch(joined, { headers: { Accept: 'text/html' } })
      .then(function (res) {
        if (!res.ok) { throw new Error('HTTP ' + res.status); }
        return res.text();
      })
      .then(function (html) {
        if (mySeq !== seq) return;
        if (!swapFromHtml(html)) { throw new Error('bad section response'); }
        state = paramsFromLocation(); /* reads fresh data-metal-tag-param post-swap */
        afterSwap();
      })
      .catch(function () {
        if (mySeq === seq) window.location.reload();
      });
  }, false);

  /* ---------------- public hook ----------------
     Lets a bespoke control (the carat range slider) drive the same filter pipeline the
     drawer checkboxes use, in ONE render, instead of faking N checkbox change events. */
  window.spxCatalog = window.spxCatalog || {};
  window.spxCatalog.setFacetValues = function (name, values) {
    if (!name) return;
    var p = cloneParams(state);
    p.delete(name);
    for (var i = 0; i < (values || []).length; i++) p.append(name, values[i]);
    p.delete('page');
    render(p);
  };
  window.spxCatalog.getFacetValues = function (name) {
    return name && state ? state.getAll(name) : [];
  };

  /* ---------------- init ---------------- */
  try { history.replaceState({ spCatalog: true }, '', window.location.href); } catch (e) {}
  if (!legacyShim()) { diamondShapeSeed(); styleTagSeed(); }
})();
